import { type FC, useState } from "react";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import { useSnackbar } from "notistack";
import { ConfirmDialog } from "@/admin/shared/ui";
import { useAddRevenueShareReportCorrectionMutation } from "@/admin/entities/revenueShareReports";

interface AddRevenueShareCorrectionDialogProps {
  open: boolean;
  reportId: string;
  currency: string;
  /**
   * Отчёт в payable уже мог лечь строкой в платёж (спека §4): корректировка
   * тогда меняет и сумму, и строки этого платежа на бэкенде. Если id
   * платежа есть, после мутации обновляем список платежей, иначе вкладка
   * «Платежи» покажет старые числа до следующего собственного рефетча.
   */
  contractPaymentId?: string | null;
  onClose: () => void;
  onCompleted?: () => void;
}

/**
 * Корректировка отчёта: сумма в валюте организации со знаком и
 * обязательное описание. Удаления корректировок нет, поэтому описание
 * обязательно: по нему потом видно, что и зачем правили.
 */
export const AddRevenueShareCorrectionDialog: FC<
  AddRevenueShareCorrectionDialogProps
> = ({ open, reportId, currency, contractPaymentId, onClose, onCompleted }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [amount, setAmount] = useState("");
  const [description, setDescription] = useState("");

  const [addCorrection, { loading, error }] =
    useAddRevenueShareReportCorrectionMutation({
      refetchQueries: contractPaymentId ? ["ContractPaymentsList"] : undefined,
      onCompleted: () => {
        enqueueSnackbar("Корректировка добавлена", { variant: "success" });
        setAmount("");
        setDescription("");
        onClose();
        onCompleted?.();
      },
      onError: () => {
        // Ошибку показывает ErrorMessage внутри ConfirmDialog.
      },
    });

  const handleConfirm = () => {
    if (!amount || !description) return;
    void addCorrection({
      variables: { id: reportId, amount: Number(amount), description },
    });
  };

  return (
    <ConfirmDialog
      open={open}
      onCancel={onClose}
      onConfirm={handleConfirm}
      confirmTitle="Корректировка отчёта"
      confirmText={`Сумма в валюте организации (${currency}). Отрицательная сумма уменьшает итог.`}
      loading={loading}
      error={error}
      disabled={!amount || !description}
    >
      <Stack spacing={2}>
        <TextField
          label="Сумма"
          type="number"
          value={amount}
          onChange={(event) => setAmount(event.target.value)}
          fullWidth
          autoFocus
        />
        <TextField
          label="Причина"
          value={description}
          onChange={(event) => setDescription(event.target.value)}
          fullWidth
          multiline
          minRows={2}
        />
      </Stack>
    </ConfirmDialog>
  );
};
